    // src/services/messagesDataService.js
    import { FirebaseFirestore } from "@capacitor-firebase/firestore";
    import {
    collection,
    doc,
    getDocs,
    onSnapshot,
    query,
    where,
    writeBatch,
    updateDoc,
    addDoc,
    serverTimestamp,
    Timestamp,
    } from "firebase/firestore";

    import { db } from "../firebase";
    import {
    getPlatform,
    isNativeFirestorePlatform,
    extractNativeDocuments,
    removeNativeSnapshotListener,
    } from "./firestorePlatform";

    const MESSAGES_COLLECTION = "nachrichten";
    const MESSAGES_REFERENCE = MESSAGES_COLLECTION;

    const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

    const toMillisSafe = (createdAtClient, createdAt) => {
    if (createdAtClient && typeof createdAtClient.toMillis === "function") {
        return createdAtClient.toMillis();
    }

    if (createdAt && typeof createdAt.toMillis === "function") {
        return createdAt.toMillis();
    }

    if (typeof createdAtClient === "string") {
        const n = Date.parse(createdAtClient);
        return Number.isFinite(n) ? n : 0;
    }

    if (typeof createdAt === "string") {
        const n = Date.parse(createdAt);
        return Number.isFinite(n) ? n : 0;
    }

    return 0;
    };

    const normalizeMessage = (id, data) => {
    const msg = {
        id,
        ...(data || {}),
    };

    if (!Array.isArray(msg.deletedFor)) {
        msg.deletedFor = [];
    }

    if (typeof msg.read !== "boolean") {
        msg.read = false;
    }

    return msg;
    };

    const extractNativeMessageDocs = (payload) => {
    const docs = extractNativeDocuments(payload);
    return Array.isArray(docs) ? docs : [];
    };

    const mapNativeMessages = (payload) => {
    const docs = extractNativeMessageDocs(payload);

    return docs.map((item, index) => {
        const id =
        item?.id ||
        item?.documentId ||
        item?.snapshot?.id ||
        item?.document?.id ||
        (typeof item?.reference === "string" ? item.reference.split("/").pop() : null) ||
        `message-${index}`;

        const data =
        item?.data ||
        item?.snapshot?.data ||
        item?.document?.data ||
        item?.value ||
        item?.fields ||
        {};

        return normalizeMessage(id, data);
    });
    };

    const buildMessagePayload = ({
    senderId,
    recipientId,
    text,
    contextType = null,
    contextId = null,
    senderEmail = "",
    recipientEmail = "",
    senderName = "",
    recipientName = "",
    }) => {
    const cleanText = String(text || "").trim();

    if (!senderId || !recipientId) {
        throw new Error("sendPlatformMessageData: senderId/recipientId fehlt");
    }

    if (!cleanText) {
        throw new Error("sendPlatformMessageData: Text ist leer");
    }

    if (isNativeFirestorePlatform()) {
        return {
        senderId,
        recipientId,
        text: cleanText,
        contextType,
        contextId,
        senderEmail: normalizeEmail(senderEmail),
        recipientEmail: normalizeEmail(recipientEmail),
        senderName: String(senderName || "").trim() || "Unbekannt",
        recipientName: String(recipientName || "").trim() || "Unbekannt",
        createdAtClient: new Date().toISOString(),
        read: false,
        deletedFor: [],
        };
    }

    return {
        senderId,
        recipientId,
        text: cleanText,
        contextType,
        contextId,
        senderEmail: normalizeEmail(senderEmail),
        recipientEmail: normalizeEmail(recipientEmail),
        senderName: String(senderName || "").trim() || "Unbekannt",
        recipientName: String(recipientName || "").trim() || "Unbekannt",
        createdAt: serverTimestamp(),
        createdAtClient: Timestamp.now(),
        read: false,
        deletedFor: [],
    };
    };

    const getAllMessagesViaWeb = async () => {
    const snap = await getDocs(collection(db, MESSAGES_COLLECTION));
    return snap.docs.map((d) => normalizeMessage(d.id, d.data()));
    };

    const getAllMessagesViaNative = async () => {
    const result = await FirebaseFirestore.getCollection({
        reference: MESSAGES_REFERENCE,
    });

    return mapNativeMessages(result);
    };

    export const getAllMessagesData = async () => {
    if (isNativeFirestorePlatform()) {
        console.log("🟢 getAllMessagesData via Native", { platform: getPlatform() });
        return await getAllMessagesViaNative();
    }

    console.log("🟢 getAllMessagesData via Web");
    return await getAllMessagesViaWeb();
    };

    export const sendPlatformMessageData = async (params) => {
    const payload = buildMessagePayload(params);

    if (isNativeFirestorePlatform()) {
        const id = `msg_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

        await FirebaseFirestore.setDocument({
        reference: `${MESSAGES_REFERENCE}/${id}`,
        data: payload,
        merge: false,
        });

        return { id };
    }

    const refDoc = await addDoc(collection(db, MESSAGES_COLLECTION), payload);
    return { id: refDoc.id };
    };

    export const getMergedInboxMessagesData = async (myUid) => {
    if (!myUid) return [];

    const allMessages = await getAllMessagesData();

    const filtered = allMessages.filter((m) => {
        const isRelevant = m.senderId === myUid || m.recipientId === myUid;
        const deletedFor = Array.isArray(m.deletedFor) ? m.deletedFor : [];
        return isRelevant && !deletedFor.includes(myUid);
    });

    return filtered
        .map((m) => ({
        ...m,
        _ts: toMillisSafe(m.createdAtClient, m.createdAt),
        }))
        .sort((a, b) => (b._ts || 0) - (a._ts || 0));
    };

    const getConversationMessagesForMeData = async (myUid, peerUid) => {
  if (!myUid || !peerUid) return [];

  if (isNativeFirestorePlatform()) {
  const sentResult = await FirebaseFirestore.getCollection({
    reference: MESSAGES_REFERENCE,
    compositeFilter: {
      type: "and",
      queryConstraints: [
        {
          type: "where",
          fieldPath: "senderId",
          opStr: "==",
          value: myUid,
        },
        {
          type: "where",
          fieldPath: "recipientId",
          opStr: "==",
          value: peerUid,
        },
      ],
    },
  });

  const receivedResult = await FirebaseFirestore.getCollection({
    reference: MESSAGES_REFERENCE,
    compositeFilter: {
      type: "and",
      queryConstraints: [
        {
          type: "where",
          fieldPath: "senderId",
          opStr: "==",
          value: peerUid,
        },
        {
          type: "where",
          fieldPath: "recipientId",
          opStr: "==",
          value: myUid,
        },
      ],
    },
  });

  const map = new Map();

  [...mapNativeMessages(sentResult), ...mapNativeMessages(receivedResult)].forEach((m) => {
    const deletedFor = Array.isArray(m.deletedFor) ? m.deletedFor : [];
    if (!deletedFor.includes(myUid)) map.set(m.id, m);
  });

  return Array.from(map.values());
}

  const qSent = query(
    collection(db, MESSAGES_COLLECTION),
    where("senderId", "==", myUid),
    where("recipientId", "==", peerUid)
  );

  const qReceived = query(
    collection(db, MESSAGES_COLLECTION),
    where("senderId", "==", peerUid),
    where("recipientId", "==", myUid)
  );

  const [sentSnap, receivedSnap] = await Promise.all([
    getDocs(qSent),
    getDocs(qReceived),
  ]);

  const map = new Map();

  sentSnap.docs.forEach((d) => {
    const msg = normalizeMessage(d.id, d.data());
    const deletedFor = Array.isArray(msg.deletedFor) ? msg.deletedFor : [];
    if (!deletedFor.includes(myUid)) map.set(d.id, msg);
  });

  receivedSnap.docs.forEach((d) => {
    const msg = normalizeMessage(d.id, d.data());
    const deletedFor = Array.isArray(msg.deletedFor) ? msg.deletedFor : [];
    if (!deletedFor.includes(myUid)) map.set(d.id, msg);
  });

  return Array.from(map.values());
};
    

    export const subscribeToInboxMergedData = async (myUid, callback, onError) => {
    if (!myUid) {
        if (typeof callback === "function") callback([]);
        return () => {};
    }

    if (typeof callback !== "function") {
        throw new Error("subscribeToInboxMergedData: callback fehlt");
    }

  if (isNativeFirestorePlatform()) {
  let inboxCache = [];
  let sentCache = [];

  let inboxListenerId = null;
  let sentListenerId = null;

  const mergeAndEmitNative = () => {
    const map = new Map();

    [...inboxCache, ...sentCache].forEach((m) => {
      if (!m?.id) return;

      const deletedFor = Array.isArray(m.deletedFor) ? m.deletedFor : [];
      if (deletedFor.includes(myUid)) return;

      map.set(m.id, m);
    });

    const merged = Array.from(map.values())
      .map((m) => ({
        ...m,
        _ts: toMillisSafe(m.createdAtClient, m.createdAt),
      }))
      .sort((a, b) => (b._ts || 0) - (a._ts || 0));

    callback(merged);
  };

  const inboxResult = await FirebaseFirestore.addCollectionSnapshotListener(
    {
  reference: MESSAGES_REFERENCE,
  compositeFilter: {
    type: "and",
    queryConstraints: [
      {
        type: "where",
        fieldPath: "recipientId",
        opStr: "==",
        value: myUid,
      },
    ],
  },
},
    (event, error) => {
      if (error) {
        console.error("🟢 Native inbox listener ERROR", error);
        inboxCache = [];
        mergeAndEmitNative();
        if (typeof onError === "function") onError(error);
        return;
      }

      inboxCache = mapNativeMessages(event);
      mergeAndEmitNative();
    }
  );

  const sentResult = await FirebaseFirestore.addCollectionSnapshotListener(
    {
  reference: MESSAGES_REFERENCE,
  compositeFilter: {
    type: "and",
    queryConstraints: [
      {
        type: "where",
        fieldPath: "senderId",
        opStr: "==",
        value: myUid,
      },
    ],
  },
},
    (event, error) => {
      if (error) {
        console.error("🟢 Native sent listener ERROR", error);
        sentCache = [];
        mergeAndEmitNative();
        if (typeof onError === "function") onError(error);
        return;
      }

      sentCache = mapNativeMessages(event);
      mergeAndEmitNative();
    }
  );

  inboxListenerId = inboxResult?.callbackId || inboxResult;
  sentListenerId = sentResult?.callbackId || sentResult;

  return async () => {
    if (inboxListenerId) await removeNativeSnapshotListener(inboxListenerId);
    if (sentListenerId) await removeNativeSnapshotListener(sentListenerId);
  };
}
        

    let inboxCache = [];
    let sentCache = [];

    const mergeAndEmit = () => {
        const map = new Map();

        [...inboxCache, ...sentCache].forEach((m) => {
        if (!m?.id) return;

        const deletedFor = Array.isArray(m.deletedFor) ? m.deletedFor : [];
        if (deletedFor.includes(myUid)) return;

        map.set(m.id, m);
        });

        const merged = Array.from(map.values())
        .map((m) => ({
            ...m,
            _ts: toMillisSafe(m.createdAtClient, m.createdAt),
        }))
        .sort((a, b) => (b._ts || 0) - (a._ts || 0));

        callback(merged);
    };

    const unsubInbox = onSnapshot(
        query(collection(db, MESSAGES_COLLECTION), where("recipientId", "==", myUid)),
        (snapshot) => {
        inboxCache = snapshot.docs.map((d) => normalizeMessage(d.id, d.data()));
        mergeAndEmit();
        },
        (error) => {
        console.error("🟢 Web inbox listener ERROR", error);
        inboxCache = [];
        mergeAndEmit();
        if (typeof onError === "function") onError(error);
        }
    );

    const unsubSent = onSnapshot(
        query(collection(db, MESSAGES_COLLECTION), where("senderId", "==", myUid)),
        (snapshot) => {
        sentCache = snapshot.docs.map((d) => normalizeMessage(d.id, d.data()));
        mergeAndEmit();
        },
        (error) => {
        console.error("🟢 Web sent listener ERROR", error);
        sentCache = [];
        mergeAndEmit();
        if (typeof onError === "function") onError(error);
        }
    );

    return () => {
        unsubInbox();
        unsubSent();
    };
    };

    export const markMessageReadData = async (messageId) => {
    if (!messageId) return;

    if (isNativeFirestorePlatform()) {
        await FirebaseFirestore.setDocument({
        reference: `${MESSAGES_REFERENCE}/${messageId}`,
        data: {
            read: true,
            updatedAtClient: new Date().toISOString(),
        },
        merge: true,
        });
        return;
    }

    await updateDoc(doc(db, MESSAGES_COLLECTION, messageId), {
        read: true,
    });
    };

   export const markConversationReadData = async (myUid, peerUid) => {
  if (!myUid || !peerUid) return;

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getCollection({
      reference: MESSAGES_REFERENCE,
      compositeFilter: {
        type: "and",
        queryConstraints: [
          {
            type: "where",
            fieldPath: "senderId",
            opStr: "==",
            value: peerUid,
          },
          {
            type: "where",
            fieldPath: "recipientId",
            opStr: "==",
            value: myUid,
          },
          {
            type: "where",
            fieldPath: "read",
            opStr: "==",
            value: false,
          },
        ],
      },
    });

    const unread = mapNativeMessages(result);

    await Promise.all(
      unread.map((m) =>
        FirebaseFirestore.setDocument({
          reference: `${MESSAGES_REFERENCE}/${m.id}`,
          data: {
            read: true,
            updatedAtClient: new Date().toISOString(),
          },
          merge: true,
        })
      )
    );

    return;
  }

  const q = query(
    collection(db, MESSAGES_COLLECTION),
    where("senderId", "==", peerUid),
    where("recipientId", "==", myUid),
    where("read", "==", false)
  );

  const snap = await getDocs(q);
  if (snap.empty) return;

  const batch = writeBatch(db);
  snap.docs.forEach((d) => {
    batch.update(doc(db, MESSAGES_COLLECTION, d.id), { read: true });
  });

  await batch.commit();
};


    export const hideConversationForMeData = async (myUid, peerUid) => {
    if (!myUid || !peerUid) return;

    const relevant = await getConversationMessagesForMeData(myUid, peerUid);

    if (relevant.length === 0) return;

    if (isNativeFirestorePlatform()) {
        await Promise.all(
        relevant.map((m) => {
            const currentDeletedFor = Array.isArray(m.deletedFor) ? m.deletedFor : [];
            const nextDeletedFor = currentDeletedFor.includes(myUid)
            ? currentDeletedFor
            : [...currentDeletedFor, myUid];

            return FirebaseFirestore.setDocument({
            reference: `${MESSAGES_REFERENCE}/${m.id}`,
            data: {
                deletedFor: nextDeletedFor,
                updatedAtClient: new Date().toISOString(),
            },
            merge: true,
            });
        })
        );
        return;
    }

    const batch = writeBatch(db);
    relevant.forEach((m) => {
        const currentDeletedFor = Array.isArray(m.deletedFor) ? m.deletedFor : [];
        const nextDeletedFor = currentDeletedFor.includes(myUid)
        ? currentDeletedFor
        : [...currentDeletedFor, myUid];

        batch.update(doc(db, MESSAGES_COLLECTION, m.id), {
        deletedFor: nextDeletedFor,
        });
    });
    await batch.commit();
    };

    export const deleteConversationHardData = async (myUid, peerUid) => {
    if (!myUid || !peerUid) return;

    const relevant = await getConversationMessagesForMeData(myUid, peerUid);

    if (relevant.length === 0) return;

    if (isNativeFirestorePlatform()) {
        await Promise.all(
        relevant.map((m) =>
            FirebaseFirestore.deleteDocument({
            reference: `${MESSAGES_REFERENCE}/${m.id}`,
            })
        )
        );
        return;
    }

    const batch = writeBatch(db);
    relevant.forEach((m) => {
        batch.delete(doc(db, MESSAGES_COLLECTION, m.id));
    });
    await batch.commit();
    };

    export const getConversationMessagesData = async (myUid, peerUid) => {
    if (!myUid || !peerUid) return [];

    const merged = await getMergedInboxMessagesData(myUid);

    return merged
        .filter((m) => {
        const a = m.senderId === myUid && m.recipientId === peerUid;
        const b = m.senderId === peerUid && m.recipientId === myUid;
        return a || b;
        })
        .sort((a, b) => (a._ts || 0) - (b._ts || 0));
    }; 